import React, { FC, useEffect } from 'react';
import { Asset, BoltIcon, Button } from '@clearcover/atlas';
import styles from './styles.module.css';
import { Spinner } from 'Shared/components';
import { useAppContext } from 'QuoteApp/services/providers';
import classNames from 'classnames';
import { reportEvent } from 'Shared/analytics';

const RewardsEstimateContainer: FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Asset
          icon={BoltIcon}
          height={20}
          width={20}
          description="Reward"
          className={styles.headerIcon}
        />
        Agent Rewards
      </div>
      {children}
    </div>
  );
};

const Rule: FC<
  | { isProcessedRule: true; rule: string; pointsEarned: number }
  | { isProcessedRule: false; ruleName: string }
> = props => {
  return (
    <div className={styles.rule}>
      <div
        className={classNames(
          styles.ruleTitle,
          !props.isProcessedRule && styles.disabledTitle
        )}
      >
        <Asset
          icon={BoltIcon}
          height={16}
          width={16}
          description="Reward"
          className={classNames(
            styles.ruleIcon,
            !props.isProcessedRule && styles.disabledIcon
          )}
        />
        {props.isProcessedRule ? props.rule : props.ruleName}
      </div>
      {props.isProcessedRule && (
        <span className={styles.ruleValue}>{`+${props.pointsEarned}`}</span>
      )}
    </div>
  );
};

export const RewardsEstimate: FC = () => {
  const {
    details,
    refreshDetails,
    detailsLoading,
    estimatePointsResponse,
    isEstimatePointsLoading,
    submitEstimatePoints,
    rewardsPointsError,
  } = useAppContext();

  useEffect(() => {
    reportEvent(
      rewardsPointsError
        ? 'purchase:rewardsEstimate:error'
        : 'purchase:rewardsEstimate:shown'
    );
  }, [rewardsPointsError]);

  useEffect(() => {
    if (details) {
      submitEstimatePoints(details);
    } else {
      refreshDetails();
    }
    // We're only concerned about estimating points if details changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  if (detailsLoading || isEstimatePointsLoading) {
    return (
      <RewardsEstimateContainer>
        <Spinner className={styles.spinner} type="gray" variant="medium" />
      </RewardsEstimateContainer>
    );
  }

  if (!isEstimatePointsLoading && !estimatePointsResponse) {
    return (
      <RewardsEstimateContainer>
        <>
          <span className={styles.youWillEarnText}>
            We Experienced an error. Don't worry - your points will be safe! Try
            again below.
          </span>
          <Button
            className={styles.retryEstimationButton}
            variant="secondary"
            onClick={() => {
              reportEvent('purchase:rewardsEstimate:retry');
              refreshDetails();
            }}
          >
            Retry Estimation
          </Button>
        </>
      </RewardsEstimateContainer>
    );
  }

  return (
    <RewardsEstimateContainer>
      <>
        <span className={styles.youWillEarnText}>You will earn</span>
        <h4 className={styles.pointsText}>
          {`${estimatePointsResponse?.totalPoints} points`}
        </h4>
        <div className={styles.rulesContainer}>
          {estimatePointsResponse?.processedRules.map(
            ({ rule, pointsEarned }) => (
              <Rule
                isProcessedRule
                rule={rule}
                pointsEarned={pointsEarned}
                key={rule}
              />
            )
          )}
          {estimatePointsResponse?.failedRules.map(({ ruleName }) => (
            <Rule isProcessedRule={false} ruleName={ruleName} key={ruleName} />
          ))}
        </div>
      </>
    </RewardsEstimateContainer>
  );
};
